/**
 * AttributeInputDialog Component
 *
 * Dialog that prompts for attribute values when a new feature is digitized.
 * Displays form fields matching the layer's attribute schema with appropriate
 * input controls for each data type.
 *
 * Validates: Requirements 48.3
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import type { AttributeField } from '../types/layerCreation';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AttributeInputDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (attributes: Record<string, any>) => void;
  fields: AttributeField[];
  title?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AttributeInputDialog: React.FC<AttributeInputDialogProps> = ({
  open,
  onClose,
  onConfirm,
  fields,
  title = 'Enter Feature Attributes',
}) => {
  const [values, setValues] = useState<Record<string, any>>({});

  // Initialize values with defaults when dialog opens
  useEffect(() => {
    if (open) {
      const defaults: Record<string, any> = {};
      for (const field of fields) {
        if (field.defaultValue !== undefined && field.defaultValue !== null) {
          defaults[field.name] = field.defaultValue;
        } else {
          switch (field.type) {
            case 'string':
              defaults[field.name] = '';
              break;
            case 'number':
              defaults[field.name] = 0;
              break;
            case 'boolean':
              defaults[field.name] = false;
              break;
            case 'date':
              defaults[field.name] = '';
              break;
          }
        }
      }
      setValues(defaults);
    }
  }, [open, fields]);

  const handleChange = useCallback((fieldName: string, value: any) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleConfirm = useCallback(() => {
    // Coerce values to proper types
    const coerced: Record<string, any> = {};
    for (const field of fields) {
      const raw = values[field.name];
      switch (field.type) {
        case 'number':
          coerced[field.name] = raw === '' ? 0 : Number(raw);
          break;
        case 'boolean':
          coerced[field.name] = Boolean(raw);
          break;
        case 'date':
          coerced[field.name] = raw || null;
          break;
        default:
          coerced[field.name] = raw ?? '';
      }
    }
    onConfirm(coerced);
  }, [values, fields, onConfirm]);

  const renderField = (field: AttributeField) => {
    const value = values[field.name];

    switch (field.type) {
      case 'boolean':
        return (
          <FormControlLabel
            key={field.name}
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleChange(field.name, e.target.checked)}
              />
            }
            label={field.name}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.name}
            label={field.name}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            fullWidth
            size="small"
          />
        );

      case 'date':
        return (
          <TextField
            key={field.name}
            label={field.name}
            type="date"
            value={value ?? ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        );

      default:
        return (
          <TextField
            key={field.name}
            label={field.name}
            value={value ?? ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            fullWidth
            size="small"
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No attribute fields defined for this layer.
            </Typography>
          ) : (
            fields.map(renderField)
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">
          Save Feature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttributeInputDialog;
